<template>
  <transition name="overlay-panel" @after-enter="$emit('opened')">
    <div v-if="show" class="overlay-backdrop" @mousedown.self="$emit('close')">
      <div
          class="overlay-panel shadow-lg"
          role="dialog"
          aria-modal="true"
          :aria-label="label"
          :style="{maxWidth}"
      >
        <slot/>
      </div>
    </div>
  </transition>
</template>

<script>
/**
 * A panel in the upper middle of the page over a dimmed backdrop, like macOS Spotlight. It fades in and out.
 * Emits close on Esc and on a click outside the panel; the parent hides it.
 */
export default {
  name: 'OverlayPanel',
  props: {
    show: Boolean,
    label: String,
    maxWidth: {
      type: String,
      default: '640px',
    },
  },
  watch: {
    show: {
      handler(show) {
        if (show) {
          document.addEventListener('keydown', this.onKeydown);
        } else {
          document.removeEventListener('keydown', this.onKeydown);
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.$emit('close');
      }
    },
  },
}
</script>

<style scoped>
.overlay-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  /* Above the sticky navbar */
  z-index: 10010;
  background-color: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 18vh 16px 16px;
}

.overlay-panel {
  width: 100%;
  background-color: rgba(255, 255, 255, 0.97);
  border-radius: 12px;
  overflow: hidden;
  text-align: left;
}

/* Fade the backdrop, and let the panel grow and drop slightly into place, like Spotlight. Closing is quicker than opening. */
.overlay-panel-enter-active,
.overlay-panel-leave-active {
  transition: opacity 180ms ease-out;
}

.overlay-panel-leave-active {
  transition-duration: 120ms;
  transition-timing-function: ease-in;
}

.overlay-panel-enter,
.overlay-panel-leave-to {
  opacity: 0;
}

.overlay-panel-enter-active .overlay-panel,
.overlay-panel-leave-active .overlay-panel {
  transition: transform 180ms ease-out, opacity 180ms ease-out;
}

.overlay-panel-leave-active .overlay-panel {
  transition-duration: 120ms;
  transition-timing-function: ease-in;
}

.overlay-panel-enter .overlay-panel,
.overlay-panel-leave-to .overlay-panel {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}

@media (prefers-reduced-motion: reduce) {
  .overlay-panel-enter-active .overlay-panel,
  .overlay-panel-leave-active .overlay-panel {
    transition: opacity 120ms linear;
  }

  .overlay-panel-enter .overlay-panel,
  .overlay-panel-leave-to .overlay-panel {
    transform: none;
  }
}
</style>
